import React, { useRef, useState } from "react";
import { useDebounceCallback } from "@react-hook/debounce";
import { connect } from "react-redux";
import { injectIntl, FormattedMessage } from "react-intl";
import ReactMarkdown from "react-markdown";
import { keys, clone, mapObjIndexed, values, prop, any, isEmpty } from "ramda";
import {} from "lodash";
import styled from "styled-components";
import {
  MultiValueForm,
  Content,
  ContentBlock,
  Checkbox,
  COLORS
} from "@onelogin/react-components";
import FormField from "./FormField";
import ProtectedSubmit from "./ProtectedSubmit";
import LargeButton from "./LargeButton";
import { register as registerAction } from "../actions/registrationActions";
import SupportLink from "./SupportLink";

const CheckboxContent = styled(ContentBlock)`
  margin-top: 0px;
  margin-bottom: 8px;
  font-size: 14px;

  div {
    color: ${COLORS.TEXT_COLORS.LABEL};
  }
`;

const Form = ({ registration, register, intl }) => {
  const formRef = useRef();

  // Checkikng that the checkbox is enabled before the Register button is clickable
  const [permission, setPermission] = useState(false);

  // Checking that all form values are filled before the Register button is clickable
  const [disabled, setDisabled] = useState(true);
  const checkDisabled = useDebounceCallback(() => {
    const formValues = values(formRef.current.state).map(prop("value"));
    setDisabled(any(isEmpty, formValues));
  }, 250);

  const { name, helpText, additionalFields } = registration;

  // Explicitly defining id list to ensure proper order of displayed fields
  const fieldIds = [
    "firstName",
    "lastName",
    "email",
    ...keys(additionalFields)
  ];

  const onSubmit = grecaptchaToken => {
    const { firstName, lastName, email, ...additional } = clone(
      formRef.current.state
    );
    register({
      name,
      grecaptchaToken,
      firstname: firstName.value,
      lastname: lastName.value,
      email: email.value,
      customValueFields: mapObjIndexed(entry => entry.value, additional)
    });
  };

  return (
    <MultiValueForm
      ref={formRef}
      fieldNames={fieldIds}
      onSubmit={() => {}} // Not using the MultiValueForm's submit because it can't be called programatically
      render={fields => (
        <Content data-testid="self-registration-form">
          <ContentBlock>
            <ReactMarkdown>{helpText}</ReactMarkdown>
          </ContentBlock>

          <FormField
            key="firstName"
            fieldId="firstName"
            fieldLabel={intl.formatMessage({
              id: "firstName",
              defaultMessage: "First Name"
            })}
            onChange={e => {
              fields.firstName.onChange(e.target.value);
              checkDisabled();
            }}
            value={fields?.firstName?.value || ""}
          />

          <FormField
            key="lastName"
            fieldId="lastName"
            fieldLabel={intl.formatMessage({
              id: "lastName",
              defaultMessage: "Last Name"
            })}
            onChange={e => {
              fields.lastName.onChange(e.target.value);
              checkDisabled();
            }}
            value={fields?.lastName?.value || ""}
          />

          <FormField
            key="email"
            fieldId="email"
            fieldLabel={intl.formatMessage({
              id: "email",
              defaultMessage: "Email"
            })}
            onChange={e => {
              fields.email.onChange(e.target.value);
              checkDisabled();
            }}
            value={fields?.email?.value || ""}
          />

          {Object.entries(additionalFields).map(([k, v]) => (
            <FormField
              key={k}
              fieldId={k}
              fieldLabel={v}
              onChange={e => {
                fields[k].onChange(e.target.value);
                checkDisabled();
              }}
              value={fields[k].value}
            />
          ))}

          <CheckboxContent>
            <Checkbox
              label={intl.formatMessage({
                id: "dataProcessingAgreement",
                defaultMessage:
                  "I agree to the processing of the personal data I am submitting"
              })}
              id="agree-with-data-processing"
              checked={permission}
              value=""
              onChange={() => setPermission(!permission)}
            />
          </CheckboxContent>

          <ContentBlock>
            <ProtectedSubmit
              renderButton={({ token }) => (
                <LargeButton
                  onClick={() => onSubmit(token)}
                  size="large"
                  look="primary"
                  disabled={!permission || disabled}
                >
                  <FormattedMessage id="submit" defaultMessage="Register" />
                </LargeButton>
              )}
            />
            <SupportLink />
          </ContentBlock>
        </Content>
      )}
    />
  );
};

const mapStateToProps = state => ({
  registration: state.registration.data
});

const mapDispatchToProps = dispatch => ({
  register: data => dispatch(registerAction(data))
});

export default injectIntl(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(Form)
);
